import React, { useState } from 'react';
import { sendContactEmail } from '../services/emailService';
import './Contact.css';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Send email using EmailJS service
      await sendContactEmail(formData);
      
      setSubmitStatus('success');
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact">
      

      {/* Contact Information */}
      <section className="section contact-info">
        <div className="container">
          <div className="grid grid-cols-2">
            <div className="contact-details">
              <h2>Get in Touch</h2>
              <div className="contact-method">
                <div className="contact-icon">📍</div>
                <div className="contact-content">
                  <h3>Office Address</h3>
                  <p>
                    Dixon Financial Services<br />
                    16 N. Gore Ave, Suite 207<br />
                    Saint Louis, MO 63119
                  </p>
                </div>
              </div>

              <div className="contact-method">
                <div className="contact-icon">📞</div>
                <div className="contact-content">
                  <h3>Phone</h3>
                  <p>
                    <a href="tel:314-961-6100">314-961-6100</a>
                  </p>
                </div>
              </div>

              <div className="contact-method">
                <div className="contact-icon">✉️</div>
                <div className="contact-content">
                  <h3>Email</h3>
                  <p>
                    <a href="mailto:rob@dixonfinancialservices.com">rob@dixonfinancialservices.com</a>
                  </p>
                </div>
              </div>

              <div className="contact-method">
                <div className="contact-icon">🕒</div>
                <div className="contact-content">
                  <h3>Office Hours</h3>
                  <div className="office-hours">
                    <p><strong>Monday - Thursday:</strong> 8:00 AM - 5:00 PM</p>
                    <p><strong>Friday:</strong> 8:00 AM - 4:00 PM</p>
                    <p><strong>Saturday:</strong> By Appointment</p>
                    <p><strong>Sunday:</strong> Closed</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-container">
              <h2>Send Us a Message</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject">Subject</label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a topic</option>
                      <option value="consultation">Schedule a Consultation</option>
                      <option value="services">Questions about Services</option>
                      <option value="existing-client">Existing Client Inquiry</option>
                      <option value="general">General Information</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Please tell us about your financial goals and how we can help you..."
                    required
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                
                {submitStatus === 'success' && (
                  <div style={{ color: '#22577A', marginTop: '1rem', padding: '0.5rem', backgroundColor: '#e8f4f8', borderRadius: '4px', border: '1px solid #22577A' }}>
                    ✓ Message sent successfully! We'll get back to you soon.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div style={{ color: '#d32f2f', marginTop: '1rem', padding: '0.5rem', backgroundColor: '#ffeaea', borderRadius: '4px', border: '1px solid #d32f2f' }}>
                    ✗ Failed to send message. Please try again or contact us directly.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section section-alt map-section">
        <div className="container">
          <h2 className="section-title">Our Location</h2>
          <div className="map-container">
            <div className="map-info">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3118.403974219731!2d-90.36192122281616!3d38.593571164572076!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87d8cad51ae57e9d%3A0x16992bd8711ca6b5!2sDixon%20Financial%20Services!5e0!3m2!1sen!2sus!4v1750266773300!5m2!1sen!2sus" 
                width="100%" 
                height="400" 
                style={{ border: 0, borderRadius: '8px', marginBottom: '1rem' }} 
                allowFullScreen={true}
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Dixon Financial Services Location"
              />
              <h3>Dixon Financial Services</h3>
              <p>16 N. Gore Ave, Suite 207<br />Saint Louis, MO 63119</p>
              <a href="https://www.google.com/maps/place/Dixon+Financial+Services/@38.5935712,-90.3619212,17z/data=!3m1!4b1!4m6!3m5!1s0x87d8cad51ae57e9d:0x16992bd8711ca6b5!8m2!3d38.5935712!4d-90.3593463!16s%2Fg%2F1tg6c4m2?entry=ttu&g_ep=EgoyMDI1MDEyMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default Contact; 